import { POLLY_LANGUAGE_OPTIONS } from '../../../data/pollyVoices';
import type { LocalizedText } from '../../../utils/localizedSsml';
import { resolveRouteNameGuides } from '../../../utils/routeNameGuide';

/**
 * 接続線の「放送で案内される線名」を放送言語ごとに表示する。
 *
 * 接続線の線区愛称は必須で、線区を選ぶとマスタの名称が自動入力される。
 * 愛称が入っていれば、放送ではその名称がそのまま案内される (画面の値 = 放送の線名)。
 * 入力欄のすぐ下に結果を出して、入力の途中で確認できるようにする。
 *
 * 表示は必要最小限にする。
 * - 愛称あり: 「湘南新宿ライン」
 * - 愛称なし (未入力): 「未入力」。保存時に入力エラーになる
 * - 言語が 1 つだけのときは言語名を出さない
 *
 * 既存データで愛称が空のまま残っている場合に限り、放送時は運行データの線名
 * (取得できなければ「<route_id>線」) で案内される。その旨を「未入力」の横に添える。
 * 規則は `utils/routeNameGuide.ts` を参照。
 */
export function RouteNameGuide({
  routeId,
  nickname,
  nicknameI18n,
  languages,
  testId,
}: {
  /** 接続線のメイン route_id。未選択なら空文字 / undefined。 */
  routeId: string | undefined;
  /** 日本語の愛称 (`route_nickname`)。 */
  nickname: unknown;
  /** 言語別の愛称 (`route_nickname_i18n`)。 */
  nicknameI18n: LocalizedText | undefined;
  /** 放送言語。 */
  languages: readonly string[];
  testId: string;
}): React.JSX.Element {
  const guides = resolveRouteNameGuides(routeId, nickname, nicknameI18n, languages);
  // 言語が 1 つだけなら言語名は不要 (余計な情報を出さない)。
  const showLanguage = guides.length > 1;
  return (
    <div className="route-name-guide" data-testid={testId} role="status">
      <span className="route-name-guide__title">放送で案内される線名</span>
      <ul className="route-name-guide__list">
        {guides.map((guide) => {
          const languageLabel =
            POLLY_LANGUAGE_OPTIONS.find((option) => option.key === guide.languageKey)?.label ??
            guide.languageKey;
          return (
            <li key={guide.languageKey} data-testid={`${testId}-${guide.languageKey}`}>
              {showLanguage ? (
                <span className="route-name-guide__lang">{languageLabel}</span>
              ) : null}
              {guide.source === 'nickname' ? (
                <strong className="route-name-guide__name">「{guide.name}」</strong>
              ) : (
                <>
                  <span className="route-name-guide__name">未入力</span>
                  <span className="route-name-guide__note">
                    （入力するまで保存できません。放送では運行データの線名が使われます）
                  </span>
                </>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
